import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
   X, Loader2,
 AlertCircle, CheckCircle2, Edit
} from "lucide-react";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getToken = () => localStorage.getItem("superadmintoken");
const getHeaders = () => ({ Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" });

const EMPTY_FORM = { shiftName: "", startTime: "", endTime: "" };

// ── Shift Modal (Add / Edit) ───────────────────────────────────────────────────
// ── Shift Modal (Add / Edit) ───────────────────────────────────────────────────
function ShiftModal({ mode, initial, onClose, onSave, companyId }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

const validate = () => {
  const newErrors = {};

  if (!String(form.shiftName || "").trim()) {
    newErrors.shiftName = "Shift Name is required";
  } else if (!String(form.startTime || "").trim()) {
    newErrors.startTime = "Start Time is required";
  } else if (!String(form.endTime || "").trim()) {
    newErrors.endTime = "End Time is required";
  } else if (form.startTime === form.endTime) {
    newErrors.endTime = "End Time must differ from Start Time";
  } else {
    const [sh, sm] = form.startTime.split(":").map(Number);
    const [eh, em] = form.endTime.split(":").map(Number);
    let diffMinutes = (eh * 60 + em) - (sh * 60 + sm);
     
    // Handle overnight shifts (e.g. 10 PM → 2 AM)
    if (diffMinutes < 0) diffMinutes += 24 * 60;
    console.log("diffMinutes:", diffMinutes, "| limit:", 12 * 60);

    if (diffMinutes > 12 * 60) {
      newErrors.endTime = "Shift must be less than 12 hours";
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const url = mode === "add"
        ? `${BASE_URL}/lenskart/add-shifts`
        : `${BASE_URL}/lenskart/update-shift/${initial.id}`;
      const method = mode === "add" ? "post" : "put";
      const res = await axios[method](url, {
        shiftName: form.shiftName,
        shiftStart: form.startTime,
        shiftEnd: form.endTime,
        companyId: companyId
      }, { headers: getHeaders() });

      if (res.data.success) {
        toast.success(`Shift ${mode === "add" ? "added" : "updated"} successfully`);
        onSave();
        onClose();
      } else {
        toast.error(res.data.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">

        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {mode === "add" ? "Add New Shift" : "Edit Shift"}
          </h3>
          <X className="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-700" onClick={onClose} />
        </div>

        <div className="mb-6 space-y-4">

          {/* Shift Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shift Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.shiftName}
              onChange={(e) => set("shiftName", e.target.value)}
              placeholder="e.g. Morning Shift"
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 ${errors.shiftName ? "border-red-400 bg-red-50" : "border-gray-300"
                }`}
            />
            {errors.shiftName && (
              <p className="text-red-500 text-xs mt-1">{errors.shiftName}</p>
            )}
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              value={form.startTime}
              onChange={(e) => set("startTime", e.target.value)}
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 ${errors.startTime ? "border-red-400 bg-red-50" : "border-gray-300"
                }`}
            />
            {errors.startTime && (
              <p className="text-red-500 text-xs mt-1">{errors.startTime}</p>
            )}
          </div>

          {/* End Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Time <span className="text-red-500">*</span>
            </label>
            <input
              type="time"
              value={form.endTime}
              onChange={(e) => set("endTime", e.target.value)}
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 ${errors.endTime ? "border-red-400 bg-red-50" : "border-gray-300"
                }`}
            />
            {errors.endTime && (
              <p className="text-red-500 text-xs mt-1">{errors.endTime}</p>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-1.5 text-white text-sm rounded-md hover:opacity-90 flex items-center justify-center gap-1"
            style={{ background: `#3B82F6` }}
          >
            {saving
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>
              : mode === "add" ? "Add Shift" : "Save Changes"
            }
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Chevron SVG ────────────────────────────────────────────────────────────────
const ChevronSVG = ({ open }) => (
  <svg
    className={`h-4 w-4 transform transition-transform ${open ? "rotate-180" : ""}`}
    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
  >
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

// ── Main Component ─────────────────────────────────────────────────────────────
export default function ManageShifts() {
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [shifts, setShifts] = useState([]);

  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");

  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingShifts, setLoadingShifts] = useState(false);

  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editShift, setEditShift] = useState(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [togglingId, setTogglingId] = useState(null);

  const filtered = shifts.filter((s) =>
    !search.trim() || s.shiftName.toLowerCase().includes(search.toLowerCase())
  );

  // Format time "HH:MM" → "H:MM AM/PM"
  const formatTime = (time) => {
    if (!time) return "";
    const [h, m] = time.split(":");
    const hour = parseInt(h);
    const ampm = hour >= 12 ? "PM" : "AM";
    return `${hour % 12 || 12}:${m} ${ampm}`;
  };

  // ── Fetch Locations ──────────────────────────────────────────────────────────
  const fetchLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const res = await axios.get(`${BASE_URL}/lenskart/locations`, { headers: getHeaders() });
      if (res.data.success && Array.isArray(res.data.locations)) {
        setLocations(res.data.locations);
      }
    } catch {
      toast.error("Failed to load locations");
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  // ── Fetch Companies ──────────────────────────────────────────────────────────
  const fetchCompanies = useCallback(async (locationId) => {
    if (!locationId) { setCompanies([]); setSelectedCompany(""); return; }
    setLoadingCompanies(true);
    setCompanies([]);
    setSelectedCompany("");
    setShifts([]);
    try {
      const res = await axios.get(
        `${BASE_URL}/OnDemandCompanies/list-companies/${locationId}`,
        {
          headers: getHeaders(),
          params: {
            isActive: true,
          },
        }

      );
      if (res.data && Array.isArray(res.data.companies)) {
        setCompanies(res.data.companies);
      }
    } catch {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  //handletoggle
  const handleToggle = async (shift) => {
    setTogglingId(shift.id);
    try {
      const token = localStorage.getItem("superadmintoken");
      const res = await axios.put(
        `${BASE_URL}/lenskart/mark-shift-active-inactive/${shift.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (res.data.success) {
        toast.success(`Shift marked as ${shift.status ? "Inactive" : "Active"}`);
        setShifts((prev) =>
          prev.map((s) => s.id === shift.id ? { ...s, status: !s.status } : s)
        );
      } else {
        toast.error(res.data.message || "Failed to update status");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
    finally {
      setTogglingId(null);
    }
  };

  // ── Fetch Shifts ─────────────────────────────────────────────────────────────
  const fetchShifts = useCallback(async (companyId) => {
    if (!companyId) { setShifts([]); return; }
    setLoadingShifts(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/lenskart/shifts?`,
        {
          headers: getHeaders(),
          params: {
            companyId: companyId,
            isActive: false
          }
        }
      );
      if (res.data.success && Array.isArray(res.data.data)) {
        setShifts(res.data.data.map((item) => ({
          id: item._id || item.id,
          shiftName: item.shiftName || item.name || "",
          startTime: item.shiftStart || "",
          endTime: item.shiftEnd || "",
          status: item.isActive === true,
        })));
      }
    } catch {
      toast.error("Failed to load shifts");
    } finally {
      setLoadingShifts(false);
    }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleLocationChange = (locId) => {
    setSelectedLocation(locId);
    setIsLocationOpen(false);
    fetchCompanies(locId);
  };

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    setIsCompanyOpen(false);
    fetchShifts(companyId);
  };

  // Helper: get location display name from _id
  const locationName = selectedLocation
    ? locations.find((l) => l._id === selectedLocation)?.name || "Select Location"
    : "Select Location";

  // Helper: get company display name from _id
const companyName = selectedCompany
  ? (() => {
      const comp = companies.find((c) => c._id === selectedCompany);
      return comp
        ? `${comp.name}${comp.companyCode ? ` (${comp.companyCode})` : ""}`
        : "Select Company";
    })()
  : "Select Company";

  // ── Location Dropdown (reusable inner JSX) ────────────────────────────────
  const LocationDropdown = (
    <div className="relative z-[100]">
      <button
        onClick={() => setIsLocationOpen((p) => !p)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center bg-white"
      >
        {locationName}
        <ChevronSVG open={isLocationOpen} />
      </button>

      {isLocationOpen && (
        <div className="absolute z-[200] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
          {loadingLocations ? (
            <div className="px-4 py-2 text-sm text-gray-500 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading...
            </div>
          ) : locations.length === 0 ? (
            <div className="px-4 py-2 text-sm text-gray-500">No locations found</div>
          ) : locations.map((loc) => (
            <button
              key={loc._id}
              onClick={() => handleLocationChange(loc._id)}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedLocation === loc._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                }`}
            >
              {loc.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ── Company Dropdown (reusable inner JSX) ─────────────────────────────────
  const CompanyDropdown = (
    <div className="relative z-[100]">
      <button
        onClick={() => { if (selectedLocation && !loadingCompanies) setIsCompanyOpen((p) => !p); }}
        disabled={!selectedLocation || loadingCompanies}
        className={`w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left flex justify-between items-center ${!selectedLocation || loadingCompanies
          ? "bg-gray-100 cursor-not-allowed text-gray-400"
          : "bg-white"
          }`}
      >
        {loadingCompanies ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading...
          </span>
        ) : companyName}
        <ChevronSVG open={isCompanyOpen} />
      </button>

      {isCompanyOpen && selectedLocation && !loadingCompanies && (
        <div className="absolute z-[200] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300">
          {companies.length > 0 ? companies.map((company) => (
            <button
              key={company._id}
              onClick={() => handleCompanyChange(company._id)}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-indigo-100 ${selectedCompany === company._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                }`}
            >
              {company.name}{company.companyCode && ` (${company.companyCode})`}
            </button>
          )) : (
            <div className="px-4 py-2 text-sm text-gray-500">No companies found</div>
          )}
        </div>
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6" style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Manage Shifts</h2>
          <p className="text-s text-gray-400">
            {loadingShifts ? "Loading..." : selectedCompany ? `Total Shifts : ${filtered.length}` : ""}
          </p>
        </div>
        {selectedCompany && (
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
          >
            Add Shift
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">{LocationDropdown}</div>
        <div className="relative flex-1">{CompanyDropdown}</div>
      </div>

      {/* Table */}
      <div className="w-full">
        <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-white shadow-md z-[50]">
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Shift Name</th>
                <th className="p-2 text-center">Start Time</th>
                <th className="p-2 text-center">End Time</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-center">Edit</th>
              </tr>
            </thead>
            <tbody>
              {!selectedCompany ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <p className="text-sm text-gray-400">
                      {!selectedLocation ? "Select a location to get started" : "Select a company to view shifts"}
                    </p>
                  </td>
                </tr>
              ) : loadingShifts ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <Loader2 size={28} className="animate-spin mx-auto mb-3 text-gray-400" />
                    <p className="text-sm text-gray-400">Loading shifts...</p>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <p className="text-sm text-gray-500 font-medium">
                      {search ? "No shifts match your search." : "No shifts found."}
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    <td className={`font-medium ${row.status ? "text-gray-800" : "text-gray-400"}`}>{row.shiftName}</td>
                    <td className="p-3 text-center text-gray-800">{formatTime(row.startTime)}</td>
                    <td className="p-3 text-center text-gray-800">{formatTime(row.endTime)}</td>
                    <td className="p-2 text-center">
                      {togglingId === row.id ? (
                        <Loader2 size={15} className="animate-spin text-gray-400 mx-auto" />
                      ) : (
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={row.status}
                            onChange={() => handleToggle(row)}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className="ml-2 text-xs font-semibold text-gray-700">Active</span>
                        </label>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <button
                        onClick={() => setEditShift(row)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 transition-all mx-auto block"
                        title="Edit shift"
                      >
                        <Edit size={14} color="#3B82F6" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <ShiftModal
          mode="add"
          companyId={selectedCompany}
          onClose={() => setShowAddModal(false)}
          onSave={() => fetchShifts(selectedCompany)}
        />
      )}

      {/* Edit Modal */}
      {editShift && (
        <ShiftModal
          mode="edit"
          initial={editShift}
          onClose={() => setEditShift(null)}
          onSave={() => fetchShifts(selectedCompany)}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Success!</h3>
              <p className="text-gray-600 mb-6">{modalMessage}</p>
              <button onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error</h3>
              <p className="text-gray-600 mb-6">{modalMessage}</p>
              <button onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                OK
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}